import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Modal, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useStyles } from './GalleryModalDual.styles';
import { Close } from '@mui/icons-material';
import clsx from 'clsx';
import { Loading } from '../Loading/Loading';

import { DropZoneImage } from './DropZoneImage/DropZoneImage';
import { TabbedGallery } from './TabbedGallery/TabbedGallery';
import EditImage from './EditImage/EditImage';
import SideBar from './SideBar/SideBar';

export const GalleryModalDual = ({
  open,
  onClose,
  setFile,
  GalleryProviderHooks,
  actionCallback,
  width,
  height,
}) => {
  const classes = useStyles();
  const [config, setConfig] = useState({ quota: 1, used: 0 });
  const [mime, setMime] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState([]);
  const [selected, setSelected] = useState(1);
  const [editMode, setEditMode] = useState();
  const [originalFile, setOriginalFile] = useState();
  const useGallery = GalleryProviderHooks.find(({ main }) => main).hook;
  const { getAllFiles, getConfigFiles, uploadFile, uploadDualImage } =
    useGallery();

  const components = useMemo(() => {
    const componentsList = [
      {
        label: 'Upload File',
        component: DropZoneImage,
        gallery: useGallery,
        type: 'dropzone',
      },
    ];

    componentsList.push(
      ...GalleryProviderHooks.map(({ hook: gallery, name, showOriginal }) => {
        return {
          label: name,
          component: TabbedGallery,
          gallery,
          type: 'gallery',
        };
      })
    );

    return componentsList;
  }, [GalleryProviderHooks, useGallery]);

  const handleEditStart = useCallback((file) => {
    setOriginalFile(file);
    setEditMode(file);
  }, []);

  const selectedComponent = useMemo(() => {
    const Component = components[selected].component;

    if (loading) return <Loading />;

    return (
      <Component
        selected={selectedFile}
        setSelected={setSelectedFile}
        mimeTypes={mime}
        actionCallback={actionCallback}
        useGallery={components[selected].gallery}
        handleEdit={handleEditStart}
      />
    );
  }, [
    components,
    selected,
    loading,
    selectedFile,
    mime,
    actionCallback,
    handleEditStart,
  ]);

  const loadConfigData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllFiles();
      const configResponse = await getConfigFiles();
      setConfig({
        quota: configResponse.max_user_quota,
        used: response.reduce((acc, cur) => acc + cur.size, 0),
      });
      setMime(configResponse);
      actionCallback({ type: 'config', status: 'success', payload: response });
    } catch (err) {
      actionCallback({ type: 'config', status: 'error', payload: err });
    } finally {
      setLoading(false);
    }
  }, [actionCallback, getAllFiles, getConfigFiles]);

  const handleSave = useCallback(() => {
    selectedFile.length > 0 && setFile(selectedFile);
    onClose();
  }, [onClose, selectedFile, setFile]);

  const handleUpdate = useCallback(async () => {
    try {
      setLoading(true);
      let resp;

      // Check if this is a newly uploaded file (has 'file' property) or existing file from gallery
      const isNewlyUploadedFile = originalFile && originalFile.file;

      if (uploadDualImage && isNewlyUploadedFile) {
        // For newly uploaded files, upload both original and edited versions
        // originalFile has the original file, editMode is the cropped blob
        resp = await uploadDualImage(originalFile, editMode);
      } else {
        // For existing files from User Uploads, only save the edited version
        resp = await uploadFile([editMode]);
      }

      setSelectedFile([resp]);
      setEditMode();
      setOriginalFile();

      // Automatically select the uploaded image and close the modal
      setFile([resp]);
      onClose();

      actionCallback({ type: 'upload', status: 'success', payload: resp });
    } catch (err) {
      actionCallback({ type: 'upload', status: 'error', payload: err });
    } finally {
      setLoading(false);
    }
  }, [
    actionCallback,
    editMode,
    originalFile,
    uploadFile,
    uploadDualImage,
    setFile,
    onClose,
  ]);

  useEffect(() => {
    loadConfigData();
  }, [loadConfigData]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={classes.wrapper}>
        <Box className={classes.container}>
          <Box className={classes.header}>
            <Typography className={classes.title}>
              {editMode ? 'Edit image' : 'Gallery & Upload'}
            </Typography>
            <IconButton onClick={onClose}>
              <Close className={classes.closeButton} />
            </IconButton>
          </Box>
          {editMode && (
            <Box className={classes.content}>
              {loading ? (
                <Loading />
              ) : (
                <EditImage
                  file={{ ...editMode }}
                  setFile={setEditMode}
                  width={width}
                  height={height}
                  actionCallback={actionCallback}
                />
              )}
            </Box>
          )}
          {!editMode && (
            <Box className={classes.content}>
              <SideBar
                components={components}
                config={config}
                setSelected={setSelected}
                selected={selected}
              />
              <Box className={classes.gallery}>{selectedComponent}</Box>
            </Box>
          )}
          <Box className={classes.footer}>
            <Button
              onClick={editMode ? () => setEditMode() : onClose}
              className={clsx(classes.button, classes.cancelButton)}
            >
              Cancel
            </Button>
            {editMode ? (
              <Button
                onClick={handleUpdate}
                className={clsx(classes.button, classes.saveButton)}
              >
                Save image
              </Button>
            ) : (
              <Button
                onClick={handleSave}
                className={clsx(classes.button, classes.saveButton)}
              >
                Save
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

GalleryModalDual.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  setFile: PropTypes.func.isRequired,
  GalleryProviderHooks: PropTypes.arrayOf(
    PropTypes.shape({
      hook: PropTypes.func,
      main: PropTypes.bool,
    })
  ).isRequired,
  actionCallback: PropTypes.func.isRequired,
  height: PropTypes.number,
  width: PropTypes.number,
};

GalleryModalDual.defaultProps = {
  open: false,
  height: 330,
  width: 630,
};

export default GalleryModalDual;
